import React, { useState, useMemo, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import useSWR from "swr";
import { toast } from "sonner";
import {
  Thermometer,
  Bug,
  PackageCheck,
  TriangleAlert,
  GraduationCap,
  SprayCan,
  CalendarRange,
  Inbox,
  Download,
  Pencil,
  Trash2,
} from "lucide-react";
import Header from "@/components/Header";
import { Card, Badge, Button } from "@/components/ui-kit";
import { listRecords, deleteRecord } from "@/lib/api";
import { getUser } from "@/lib/auth";

const TYPES = [
  { key: "temperature", label: "Temperatures", Icon: Thermometer, color: "terracotta" },
  { key: "pest", label: "Plagues", Icon: Bug, color: "forest" },
  { key: "reception", label: "Recepció", Icon: PackageCheck, color: "ink" },
  { key: "incidence", label: "Incidències", Icon: TriangleAlert, color: "ember" },
  { key: "training", label: "Formació", Icon: GraduationCap, color: "ink" },
  { key: "cleaning", label: "Neteja", Icon: SprayCan, color: "forest" },
];

const COLOR_MAP = {
  terracotta: "bg-terracotta text-white",
  forest: "bg-forest text-white",
  ink: "bg-ink text-white",
  ember: "bg-ember text-white",
};

// Columnes per a l'exportació CSV, per tipus de registre
const CSV_COLUMNS = {
  temperature: (r) => ({ Data: r.date, Firmant: r.signer, Lectures: (r.readings || []).map((x) => `${x.equipment_name}: ${x.temperature}°C (${x.status})`).join(" | "), Observacions: r.notes || "" }),
  pest: (r) => ({ Data: r.date, Firmant: r.signer, Resultat: r.visual_result, Mesura: r.measure_taken || "", Empresa: r.pest_company || "", Albarà: r.delivery_note || "" }),
  reception: (r) => ({ Data: r.date, Firmant: r.signer, Proveïdor: r.provider, Albarà: r.delivery_note, Producte: r.product || (r.lines || []).map((l) => l.product).join("; "), Quantitat: r.quantity || (r.lines || []).map((l) => `${l.quantity}${l.unit || ""}`).join("; "), Lot: r.lot || (r.lines || []).map((l) => l.lot).filter(Boolean).join("; "), Caducitat: r.expiry || (r.lines || []).map((l) => l.expiry).filter(Boolean).join("; "), "Temp recepció": r.reception_temp, "Límit": r.max_temp, Validació: r.validation, Incidència: r.incident || "" }),
  incidence: (r) => ({ Data: r.date, Firmant: r.signer, Pla: r.affected_plan, Descripció: r.description, Mesura: r.corrective_measure, Seguiment: r.follow_up || "" }),
  training: (r) => ({ Data: r.date, Firmant: r.signer, Activitat: r.activity_name, Persona: r.person_name, "Lloc de treball": r.position, Tipus: r.training_type === "I" ? "Inicial" : "Contínua", Hores: r.hours, Empresa: r.training_company, Certificat: r.cert_code || "" }),
  cleaning: (r) => ({ Data: r.date, Firmant: r.signer, Zona: r.zone, Freqüència: r.frequency, Producte: r.product_used, Mètode: r.method || "", Validació: r.validation, Incidència: r.incident || "" }),
};

export default function History() {
  const [params, setParams] = useSearchParams();
  const [type, setType] = useState(params.get("type") || "temperature");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");

  useEffect(() => {
    setParams({ type }, { replace: true });
  }, [type, setParams]);

  const swrKey = useMemo(
    () => ["records", type, start, end].join("|"),
    [type, start, end]
  );

  const { data, isLoading, mutate } = useSWR(swrKey, () =>
    listRecords(type, {
      ...(start && { start }),
      ...(end && { end }),
    })
  );

  const navigate = useNavigate();
  const isAdmin = getUser()?.rol === "admin";
  const currentType = TYPES.find((t) => t.key === type);

  const downloadCSV = () => {
    if (!data?.length) return;
    const rows = data.map(CSV_COLUMNS[type]);
    const headers = Object.keys(rows[0]);
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [headers.map(esc).join(";"), ...rows.map((r) => headers.map((h) => esc(r[h])).join(";"))].join("\r\n");
    const BOM = String.fromCharCode(0xfeff);
    const blob = new Blob([BOM + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${type}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const onEdit = (rec) => navigate(`/fill/${type}`, { state: { editRecord: rec } });
  const onDelete = async (rec) => {
    if (!window.confirm("Eliminar aquest registre? No es pot desfer.")) return;
    try { await deleteRecord(type, rec.id); toast.success("Registre eliminat"); mutate(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error eliminant"); }
  };

  return (
    <>
      <Header title="Històric" />
      <div className="mx-auto max-w-3xl px-4 pb-16 pt-6 sm:px-6 sm:pt-10">
        <div className="animate-fade-up">
          <div className="label-eyebrow">Històric</div>
          <h2 className="mt-2 font-display text-3xl font-bold leading-tight text-ink sm:text-4xl">
            Registres anteriors
          </h2>
        </div>

        {/* Type tabs */}
        <div className="mt-6 -mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
          <div className="flex gap-2">
            {TYPES.map((t) => (
              <button
                key={t.key}
                data-testid={`history-tab-${t.key}`}
                onClick={() => setType(t.key)}
                className={
                  "flex h-11 shrink-0 items-center gap-2 rounded-full border px-4 text-sm font-semibold transition-all " +
                  (type === t.key
                    ? "border-ink bg-ink text-white"
                    : "border-line bg-white text-ink-soft hover:border-ink/30")
                }
              >
                <t.Icon size={16} />
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Date filters */}
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div>
            <div className="label-eyebrow mb-2 flex items-center gap-1.5">
              <CalendarRange size={11} /> Des de
            </div>
            <input
              data-testid="filter-start"
              type="date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              className="h-12 w-full rounded-xl border border-line bg-white px-3 text-sm text-ink outline-none focus:border-terracotta focus:ring-2 focus:ring-terracotta/20"
            />
          </div>
          <div>
            <div className="label-eyebrow mb-2 flex items-center gap-1.5">
              <CalendarRange size={11} /> Fins a
            </div>
            <input
              data-testid="filter-end"
              type="date"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              className="h-12 w-full rounded-xl border border-line bg-white px-3 text-sm text-ink outline-none focus:border-terracotta focus:ring-2 focus:ring-terracotta/20"
            />
          </div>
        </div>

        {/* Descàrrega CSV */}
        <div className="mt-4 flex items-center justify-between gap-3">
          <div className="text-sm text-ink-soft">{data?.length ?? 0} registres</div>
          <Button variant="secondary" onClick={downloadCSV} disabled={!data?.length} testid="history-csv">
            <Download size={16} /> Descarregar CSV
          </Button>
        </div>

        {/* Results */}
        <div className="mt-6 space-y-3" data-testid="history-results">
          {isLoading && (
            <Card className="text-center text-sm text-ink-soft">Carregant…</Card>
          )}
          {!isLoading && (data?.length ?? 0) === 0 && (
            <EmptyState type={currentType} />
          )}
          {!isLoading &&
            data?.map((rec) => (
              <RecordRow key={rec.id} type={type} record={rec} Icon={currentType.Icon} color={currentType.color}
                isAdmin={isAdmin} onEdit={() => onEdit(rec)} onDelete={() => onDelete(rec)} />
            ))}
        </div>
      </div>
    </>
  );
}

function EmptyState({ type }) {
  return (
    <Card className="flex flex-col items-center gap-3 py-12 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-muted text-ink-soft">
        <Inbox size={26} />
      </div>
      <div className="font-display text-lg font-semibold text-ink">
        Encara no hi ha registres
      </div>
      <div className="max-w-xs text-sm text-ink-soft">
        Quan facis el primer registre de <strong>{type?.label}</strong>, apareixerà
        aquí.
      </div>
    </Card>
  );
}

function RecordRow({ type, record, Icon, color, isAdmin, onEdit, onDelete }) {
  const dateLabel = new Date(record.date).toLocaleDateString("ca-ES", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  return (
    <Card testid={`record-${record.id}`} className="animate-fade-up">
      <div className="flex items-start gap-3">
        <div
          className={
            "flex h-11 w-11 shrink-0 items-center justify-center rounded-xl " +
            COLOR_MAP[color]
          }
        >
          <Icon size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="font-display text-base font-bold text-ink">
                {dateLabel}
              </div>
              <div className="text-xs text-ink-soft">
                Signat per <strong>{record.signer}</strong>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <SummaryBadge type={type} record={record} />
              {isAdmin && (
                <>
                  <button onClick={onEdit} className="flex h-8 w-8 items-center justify-center rounded-full text-ink-soft hover:bg-muted hover:text-ink" aria-label="Editar"><Pencil size={15} /></button>
                  <button onClick={onDelete} className="flex h-8 w-8 items-center justify-center rounded-full text-ink-soft hover:bg-ember-bg hover:text-ember" aria-label="Eliminar"><Trash2 size={15} /></button>
                </>
              )}
            </div>
          </div>
          <div className="mt-3">
            <RecordBody type={type} record={record} />
          </div>
        </div>
      </div>
    </Card>
  );
}

function SummaryBadge({ type, record }) {
  if (type === "temperature") {
    const anyDef = record.readings?.some((r) => r.status === "DEFICIENCIA");
    return <Badge status={anyDef ? "DEFICIENCIA" : "OK"} />;
  }
  if (type === "reception") return <Badge status={record.validation} />;
  if (type === "pest")
    return (
      <Badge
        status={record.visual_result === "sense_indici" ? "OK" : "DEFICIENCIA"}
      />
    );
  if (type === "cleaning") return <Badge status={record.validation} />;
  return null;
}

function RecordBody({ type, record }) {
  if (type === "temperature") {
    return (
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        {record.readings?.map((r) => (
          <div
            key={r.equipment_id}
            className={
              "rounded-lg px-3 py-2 text-xs " +
              (r.status === "DEFICIENCIA"
                ? "bg-ember-bg text-ember"
                : "bg-muted text-ink-soft")
            }
          >
            <div className="font-semibold">{r.equipment_name}</div>
            <div className="font-mono text-base text-ink">{r.temperature}°C</div>
          </div>
        ))}
        {record.notes && (
          <div className="rounded-lg bg-muted px-3 py-2 text-xs text-ink-soft sm:col-span-3">
            <span className="font-semibold">Observacions:</span> {record.notes}
          </div>
        )}
      </div>
    );
  }
  if (type === "pest") {
    return (
      <div className="space-y-1 text-sm text-ink-soft">
        <div>
          Revisió:{" "}
          <span className="font-medium text-ink">
            {record.visual_result === "sense_indici"
              ? "Cap indici"
              : "Indici detectat"}
          </span>
        </div>
        {record.measure_taken && (
          <div className="text-xs">
            <span className="font-semibold">Mesura:</span> {record.measure_taken}
          </div>
        )}
        {record.pest_company && (
          <div className="text-xs">
            <span className="font-semibold">Empresa:</span> {record.pest_company}
          </div>
        )}
      </div>
    );
  }
  if (type === "reception") {
    return (
      <div className="space-y-1 text-sm text-ink-soft">
        <div className="text-ink">
          <strong>{record.product}</strong> · {record.quantity}
        </div>
        <div className="text-xs">
          Proveïdor: {record.provider} · Albarà {record.delivery_note}
        </div>
        <div className="text-xs">
          Temp. recepció:{" "}
          <span className="font-mono text-ink">{record.reception_temp}°C</span>{" "}
          (límit ≤{record.max_temp}°C)
        </div>
        {(record.lot || record.expiry) && (
          <div className="text-xs">
            {record.lot && <>Lot: <span className="text-ink">{record.lot}</span> </>}
            {record.expiry && <>· Caducitat: <span className="text-ink">{record.expiry}</span></>}
          </div>
        )}
        {record.incident && (
          <div className="text-xs text-ember">
            <span className="font-semibold">Incidència:</span> {record.incident}
          </div>
        )}
        {record.photos?.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {record.photos.map((url) => (
              <a key={url} href={url} target="_blank" rel="noopener noreferrer">
                <img src={url} alt="Albarà" className="h-14 w-14 rounded-lg border border-line object-cover" />
              </a>
            ))}
          </div>
        )}
      </div>
    );
  }
  if (type === "incidence") {
    return (
      <div className="space-y-1 text-sm text-ink-soft">
        <div className="text-xs font-semibold uppercase tracking-wide text-ember">
          {record.affected_plan}
        </div>
        <div className="text-ink">{record.description}</div>
        <div className="text-xs">
          <span className="font-semibold">Mesura:</span> {record.corrective_measure}
        </div>
        {record.follow_up && (
          <div className="text-xs">
            <span className="font-semibold">Seguiment:</span> {record.follow_up}
          </div>
        )}
      </div>
    );
  }
  if (type === "training") {
    return (
      <div className="space-y-1 text-sm text-ink-soft">
        <div className="text-ink">
          <strong>{record.activity_name}</strong>
        </div>
        <div className="text-xs">
          {record.person_name} · {record.position}
        </div>
        <div className="text-xs">
          {record.training_type === "I" ? "Inicial" : "Contínua"} ·{" "}
          {record.hours}h · {record.training_company}
          {record.cert_code && ` · Cert. ${record.cert_code}`}
        </div>
      </div>
    );
  }
  if (type === "cleaning") {
    return (
      <div className="space-y-1 text-sm text-ink-soft">
        <div className="text-ink">
          <strong>{record.zone}</strong> · {record.frequency}
        </div>
        <div className="text-xs">Producte: {record.product_used}</div>
        {record.method && (
          <div className="text-xs">
            <span className="font-semibold">Mètode:</span> {record.method}
          </div>
        )}
        {record.incident && (
          <div className="text-xs text-ember">
            <span className="font-semibold">Incidència:</span> {record.incident}
          </div>
        )}
        {record.photos?.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {record.photos.map((url) => (
              <a key={url} href={url} target="_blank" rel="noopener noreferrer">
                <img
                  src={url}
                  alt="Foto neteja"
                  className="h-16 w-16 rounded-lg border border-line object-cover"
                />
              </a>
            ))}
          </div>
        )}
      </div>
    );
  }
  return null;
}
